/**
 * Converts the first character of the given string to uppercase and the remaining characters to lowercase.
 *
 * @param str - The input string to be converted.
 * @returns The converted string with the first character in uppercase and the rest in lowercase.
 */
export const startCase = (str: string): string => {
  return `${str?.slice(0, 1).toUpperCase()}${str?.slice(1).toLowerCase()}`;
};

/**
 * Generates a short, readable hash from a string (djb2 algorithm, 8 hex chars).
 * @param str - The input string to hash.
 * @returns A short hash string.
 */
export function shortStringHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  // Convert to positive 32-bit integer and hex
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Truncates a string in the middle with ellipsis if it exceeds maxLength.
 * @param text - The input string to truncate.
 * @param maxLength - The maximum length of the resulting string (including ellipsis).
 * @returns The truncated string with '...' in the middle, or the original string if shorter than maxLength.
 */
export const truncateMiddle = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  const charsToShow = maxLength - 3; // Account for '...'
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);
  const endPart = backChars > 0 ? text.slice(-backChars) : '';
  return `${text.slice(0, frontChars)}...${endPart}`;
};
